/** Per-account mail signatures. Settings → Mail. The account copy lives on the Hub API. */

import { isBlankHtml, plainToHtml } from "@/mail/htmlBody";

export const MAIL_SIGNATURES_KEY = "atw-hub-mail-signatures";
export const MAIL_SIGNATURE_ENABLED_KEY = "atw-hub-mail-signature-enabled";
export const MAIL_SIGNATURE_EVENT = "atw-hub-mail-signature";

/** Used before any mailbox is connected, and as a fallback until an account has its own. */
export const MAIL_SIGNATURE_DEFAULT_ID = "*";

/** Room for formatted text plus a small logo. Larger photos are refused in the editor. */
const MAX_SIGNATURE_CHARS = 180_000;
const SIGNATURE_MARK = 'data-hub-signature="1"';

export type MailSignatureUse = "new" | "reply";

export type MailSignaturePair = {
  create: string;
  reply: string;
};

type SignatureFields = {
  create?: string;
  reply?: string;
};

type SignatureStore = Record<string, string | SignatureFields>;

function isSignatureFields(value: unknown): value is SignatureFields {
  return Boolean(value) && typeof value === "object" && !Array.isArray(value);
}

/** A plain string from older saves applies to both new mail and replies. */
export function signaturePairFromStored(value: unknown): MailSignaturePair {
  if (typeof value === "string") return { create: value, reply: value };
  if (isSignatureFields(value)) {
    return {
      create: typeof value.create === "string" ? value.create : "",
      reply: typeof value.reply === "string" ? value.reply : "",
    };
  }
  return { create: "", reply: "" };
}

function readRaw(key: string): string | null {
  try {
    return localStorage.getItem(key);
  } catch {
    return null;
  }
}

function readStore(): SignatureStore {
  const raw = readRaw(MAIL_SIGNATURES_KEY);
  if (!raw) return {};
  try {
    const parsed = JSON.parse(raw) as unknown;
    if (!parsed || typeof parsed !== "object" || Array.isArray(parsed)) return {};
    const out: SignatureStore = {};
    for (const [key, value] of Object.entries(parsed)) {
      if (typeof value === "string") out[key] = value;
      else if (isSignatureFields(value)) out[key] = signaturePairFromStored(value);
    }
    return out;
  } catch {
    return {};
  }
}

function writeStore(store: SignatureStore) {
  try {
    localStorage.setItem(MAIL_SIGNATURES_KEY, JSON.stringify(store));
  } catch {
    /* ignore quota / private mode */
  }
  try {
    window.dispatchEvent(new CustomEvent(MAIL_SIGNATURE_EVENT));
  } catch {
    /* node selftests */
  }
}

export function clampMailSignature(text: string): string {
  return text.slice(0, MAX_SIGNATURE_CHARS);
}

export function sanitizeSignatureHtml(html: string): string {
  return html
    .replace(/<script\b[^>]*>[\s\S]*?<\/script>/gi, "")
    .replace(/\son\w+\s*=\s*("[^"]*"|'[^']*'|[^\s>]+)/gi, "");
}

function looksLikeHtml(value: string): boolean {
  return /<\/?[a-z][^>]*>/i.test(value);
}

/** Plain text from older saves becomes HTML. Markup from the signature editor is kept. */
export function signatureInnerHtml(stored: string): string {
  const trimmed = stored.replace(/\r\n/g, "\n").trim();
  if (!trimmed) return "";
  const html = looksLikeHtml(trimmed) ? trimmed : plainToHtml(trimmed);
  return sanitizeSignatureHtml(html);
}

/** On by default. Off keeps the saved text but stops inserting it. */
export function readMailSignatureEnabled(): boolean {
  return readRaw(MAIL_SIGNATURE_ENABLED_KEY) !== "off";
}

export function writeMailSignatureEnabled(on: boolean) {
  try {
    localStorage.setItem(MAIL_SIGNATURE_ENABLED_KEY, on ? "on" : "off");
  } catch {
    /* ignore */
  }
  try {
    window.dispatchEvent(new CustomEvent(MAIL_SIGNATURE_EVENT));
  } catch {
    /* node selftests */
  }
}

function storedPair(store: SignatureStore, accountId: string): MailSignaturePair | null {
  if (!Object.prototype.hasOwnProperty.call(store, accountId)) return null;
  return signaturePairFromStored(store[accountId]);
}

/** Only this mailbox. A blank personal mailbox stays blank even if another mailbox has a signature. */
function pairForAccount(accountId: string): MailSignaturePair | null {
  const store = readStore();
  const id = accountId || MAIL_SIGNATURE_DEFAULT_ID;
  return storedPair(store, id);
}

/** Exact pair saved for this account. Empty when that account has never been set. */
export function readStoredMailSignaturePair(accountId: string): MailSignaturePair {
  return storedPair(readStore(), accountId) ?? { create: "", reply: "" };
}

/** Exact new-mail text saved for this account. Empty when that account has never been set. */
export function readStoredMailSignature(accountId: string): string {
  return readStoredMailSignaturePair(accountId).create;
}

export function writeMailSignature(accountId: string, text: string) {
  writeMailSignaturePair(accountId, { create: text, reply: text });
}

export function writeMailSignaturePair(accountId: string, pair: MailSignaturePair) {
  const id = accountId || MAIL_SIGNATURE_DEFAULT_ID;
  const store = readStore();
  const create = normalizeSignatureField(pair.create);
  const reply = normalizeSignatureField(pair.reply);
  store[id] = { create, reply };
  writeStore(store);
}

function normalizeSignatureField(html: string): string {
  const clean = clampMailSignature(sanitizeSignatureHtml(html));
  return isBlankHtml(clean) ? "" : clean;
}

export type AccountSignatureStatus = "saved" | "empty";

/** Saved on this mailbox, or nothing. */
export function accountSignatureStatus(accountId: string): AccountSignatureStatus {
  const own = storedPair(readStore(), accountId);
  if (
    own &&
    (!isBlankHtml(signatureInnerHtml(own.create)) || !isBlankHtml(signatureInnerHtml(own.reply)))
  ) {
    return "saved";
  }
  return "empty";
}

function editorPair(pair: MailSignaturePair): MailSignaturePair {
  return {
    create: signatureInnerHtml(pair.create),
    reply: signatureInnerHtml(pair.reply),
  };
}

/** HTML to show in the editors for this mailbox. */
export function signatureEditorPair(accountId: string): MailSignaturePair {
  const pair = pairForAccount(accountId);
  if (!pair) return { create: "", reply: "" };
  return editorPair(pair);
}

/** HTML to show in the editor for this mailbox. */
export function signatureEditorHtml(accountId: string): string {
  return signatureEditorPair(accountId).create;
}

export type MailSignatureSource = "account" | "none";

/** Whether compose will use this account's text, or nothing. */
export function mailSignatureSource(accountId: string): MailSignatureSource {
  const pair = signatureEditorPair(accountId);
  return pair.create.trim() || pair.reply.trim() ? "account" : "none";
}

/** Signature inserted for this From account. New mail and replies are stored separately. */
export function readMailSignature(accountId: string, use: MailSignatureUse = "new"): string {
  if (!readMailSignatureEnabled()) return "";
  const pair = pairForAccount(accountId);
  if (!pair) return "";
  return (use === "reply" ? pair.reply : pair.create).trim();
}

/** Stable HTML block so a From change can replace an untouched signature. */
export function mailSignatureHtml(stored: string): string {
  const inner = signatureInnerHtml(stored).trim();
  if (!inner || isBlankHtml(inner)) return "";
  return `<div ${SIGNATURE_MARK}>${inner}</div>`;
}

/**
 * Place the signature under the typing space and above quoted or forwarded text.
 * A blank line separates the signature from that quoted reply.
 * A message the user already started (no leading blank lines) gets the signature at the end.
 */
export function insertMailSignature(bodyHtml: string, signatureText: string): string {
  const block = mailSignatureHtml(signatureText);
  if (!block) return bodyHtml;
  if (bodyHtml.includes(SIGNATURE_MARK)) return bodyHtml;
  if (!bodyHtml.trim()) return `<p><br></p><p><br></p>${block}`;

  const lead = bodyHtml.match(/^(?:<p><br><\/p>)+/);
  if (lead) {
    const blanks = lead[0].match(/<p><br><\/p>/g)?.length ?? 0;
    const prefix = "<p><br></p>".repeat(Math.max(2, blanks));
    const rest = bodyHtml.slice(lead[0].length);
    const gap = rest.trim() ? "<p><br></p>" : "";
    return `${prefix}${block}${gap}${rest}`;
  }
  return `${bodyHtml}${block}`;
}

const SIGNATURE_OPEN = /<div\b[^>]*\bdata-hub-signature\s*=\s*(["'])1\1[^>]*>/i;

/**
 * Find the full signature wrapper, including nested divs inside a table layout.
 * A non-greedy `.*?<\/div>` stops at the first inner </div> and leaves the photo
 * or title behind when From changes.
 */
export function findMailSignatureRange(html: string): { start: number; end: number } | null {
  const open = SIGNATURE_OPEN.exec(html);
  if (!open || open.index == null) return null;
  const start = open.index;
  let i = start + open[0].length;
  let depth = 1;
  while (i < html.length && depth > 0) {
    const nextOpen = html.slice(i).search(/<div\b/i);
    const nextClose = html.slice(i).search(/<\/div>/i);
    if (nextClose < 0) return null;
    if (nextOpen >= 0 && nextOpen < nextClose) {
      depth += 1;
      i += nextOpen + 4;
      continue;
    }
    depth -= 1;
    i += nextClose + 6;
  }
  if (depth !== 0) return null;
  return { start, end: i };
}

function replaceMailSignatureBlock(html: string, nextBlock: string): string {
  const range = findMailSignatureRange(html);
  if (!range) return html;
  return `${html.slice(0, range.start)}${nextBlock}${html.slice(range.end)}`;
}

function stripMailSignatureBlocks(html: string): string {
  let out = html;
  for (;;) {
    const range = findMailSignatureRange(out);
    if (!range) return out;
    out = `${out.slice(0, range.start)}${out.slice(range.end)}`;
  }
}

/**
 * Replace the inserted signature when From changes.
 * Match the whole signature wrapper, not the first nested </div>, so a table
 * signature with a photo does not leave title lines behind.
 */
export function swapMailSignature(bodyHtml: string, _previousText: string, nextText: string): string {
  const next = mailSignatureHtml(nextText);
  if (findMailSignatureRange(bodyHtml)) {
    if (next && bodyHtml.includes(next)) return bodyHtml;
    return replaceMailSignatureBlock(bodyHtml, next);
  }
  if (next) return insertMailSignature(bodyHtml, nextText);
  return bodyHtml;
}

/** True when the only content is the inserted signature (safe to close without a discard prompt). */
export function composeBodyIsBlank(html: string): boolean {
  return isBlankHtml(stripMailSignatureBlocks(html));
}
